import { useCallback, useRef } from "react";
import type {
	ClipboardEvent,
	ClipboardEventHandler,
	DragEvent,
	DragEventHandler,
	FocusEvent,
	FocusEventHandler,
	KeyboardEvent,
	KeyboardEventHandler,
	MouseEvent,
	MouseEventHandler,
} from "react";

/* ------------------------------------------------------------------ *
 * Character filtering
 * ------------------------------------------------------------------ */

/**
 * Every character a numeric expression is made of: the digits, both decimal
 * separators, the operators and the handwritten spellings of them. The
 * keystroke filter and the paste filter share the set, so a character that
 * cannot be typed into the field cannot be pasted into it either.
 *
 * `x` and `:` belong to it because "12x3" and "12:3" are what a multiplication
 * and a division look like when they are written by hand, and `normalize` maps
 * them onto `*` and `/`. Dropping the letter instead would read "12x3" as 123 —
 * a silent wrong answer, which is worse than either interpretation.
 */
const EXPRESSION_CHARACTER = /[\d\s.,+\-*/():x×✕·÷–—−]/i;

/** Drops every character an expression cannot contain, letters included. */
function keepExpressionCharacters(raw: string): string {
	return Array.from(raw)
		.filter((character) => EXPRESSION_CHARACTER.test(character))
		.join("");
}

/* ------------------------------------------------------------------ *
 * Expression parsing (recursive descent, no eval, separator-aware)
 * ------------------------------------------------------------------ */

type Cursor = { s: string; i: number };

/**
 * Rewrite one run of digits and separators as `1234.56`, deciding per number
 * which separator is the decimal point: with both kinds present the last one
 * decides and the rest group, so "1.234,56" and "1,234.56" both read as
 * 1234.56, while a single separator is always a decimal point, so "1,5" and
 * "1.5" stay one and a half. "1.234" is therefore one and a bit rather than a
 * thousand — a grouped number only becomes unambiguous once its second
 * separator arrives.
 */
function canonicalizeNumber(token: string): string {
	if (!/\d/.test(token)) return "";

	const separators = (token.match(/[.,]/g) ?? []).length;
	if (separators === 0) return token;

	const lastComma = token.lastIndexOf(",");
	const lastDot = token.lastIndexOf(".");
	const hasBothKinds = lastComma >= 0 && lastDot >= 0;
	const decimalAt = hasBothKinds || separators === 1 ? Math.max(lastComma, lastDot) : -1;

	const digitsOf = (part: string) => part.replace(/[.,]/g, "");
	if (decimalAt < 0) return digitsOf(token);
	return `${digitsOf(token.slice(0, decimalAt))}.${digitsOf(token.slice(decimalAt + 1))}`;
}

/**
 * Reduce text to the parser's alphabet — digits, a dot decimal point and the
 * five operator characters. The handwritten operators are mapped onto their
 * ASCII forms first so they survive the drop that follows, and everything the
 * parser has no meaning for (letters, currency signs, whitespace) is removed.
 */
function normalize(raw: string): string {
	return raw
		.replace(/[×✕·x]/gi, "*")
		.replace(/[÷:]/g, "/")
		.replace(/[–—−]/g, "-")
		.replace(/[^\d.,+\-*/()]/g, "")
		.replace(/[\d.,]+/g, canonicalizeNumber);
}

function parseNumber(c: Cursor): number {
	const start = c.i;
	while (/[\d.]/.test(c.s[c.i] ?? "")) c.i++;
	if (c.i === start) throw new Error("expected number");
	const n = Number(c.s.slice(start, c.i));
	if (!Number.isFinite(n)) throw new Error("malformed number");
	return n;
}

function parseFactor(c: Cursor): number {
	const ch = c.s[c.i];
	if (ch === "-") {
		c.i++;
		return -parseFactor(c);
	}
	if (ch === "+") {
		c.i++;
		return parseFactor(c);
	}
	if (ch === "(") {
		c.i++;
		const v = parseExpr(c);
		if (c.s[c.i] !== ")") throw new Error("unbalanced parenthesis");
		c.i++;
		return v;
	}
	return parseNumber(c);
}

function parseTerm(c: Cursor): number {
	let v = parseFactor(c);
	for (;;) {
		const ch = c.s[c.i];
		if (ch === "*") {
			c.i++;
			v *= parseFactor(c);
		} else if (ch === "/") {
			c.i++;
			const d = parseFactor(c);
			if (d === 0) throw new Error("division by zero");
			v /= d;
		} else {
			return v;
		}
	}
}

function parseExpr(c: Cursor): number {
	let v = parseTerm(c);
	for (;;) {
		const ch = c.s[c.i];
		if (ch === "+") {
			c.i++;
			v += parseTerm(c);
		} else if (ch === "-") {
			c.i++;
			v -= parseTerm(c);
		} else {
			return v;
		}
	}
}

function evaluate(normalized: string): number | null {
	if (!normalized) return null;
	try {
		const c: Cursor = { s: normalized, i: 0 };
		const v = parseExpr(c);
		if (c.i !== normalized.length) return null; // trailing garbage
		return Number.isFinite(v) ? v : null;
	} catch {
		return null;
	}
}

/* ------------------------------------------------------------------ *
 * Repair
 * ------------------------------------------------------------------ */

/** Drops the closers nothing opened, and closes what the text left open. */
function balanceParentheses(raw: string): string {
	let depth = 0;
	let out = "";
	for (const character of raw) {
		if (character === "(") depth++;
		else if (character === ")") {
			if (depth === 0) continue;
			depth--;
		}
		out += character;
	}
	return out + ")".repeat(depth);
}

/**
 * Turn a normalized string the parser rejects into one it accepts, the way a
 * pocket calculator reads a fumbled keypress: an operator run keeps its first
 * operator, a trailing operator is an unfinished keystroke and goes, a missing
 * parenthesis is closed, an empty group is nothing at all, and a number beside
 * a group multiplies. The result may still be empty — "**" carries no number to
 * keep.
 */
function repair(normalized: string): string {
	let out = normalized;

	// "5**3" is a multiplication typed twice, "5*/3" a corrected one.
	while (/[+\-*/(][*/]/.test(out)) out = out.replace(/([+\-*/(])[*/]+/g, "$1");
	out = out.replace(/^[*/]+/, "");

	// "2(3+4)" and "(3+4)2" are multiplications on paper.
	out = out.replace(/(\d|\))\(/g, "$1*(").replace(/\)(\d)/g, ")*$1");

	out = balanceParentheses(out);
	while (out.includes("()")) out = out.replace(/\(\)/g, "");

	return out.replace(/[+\-*/]+$/, "");
}

/* ------------------------------------------------------------------ *
 * Coercion
 * ------------------------------------------------------------------ */

/** Returns the numeric result, or null if the text is not a valid expression. */
export function evaluateExpression(raw: string): number | null {
	return evaluate(normalize(raw));
}

/**
 * The number behind any text. `evaluateExpression` answers whether the text is
 * a valid calculation; this answers what it is worth, which is what a numeric
 * field always needs: the text is reduced to the expression alphabet, repaired
 * when the parser rejects it, and failing that the first number in it is taken.
 * Only text carrying no digit at all — "abc", "**", an empty field — comes back
 * null, and that is the one case a caller has to decide for itself.
 */
export function coerceToNumber(raw: string): number | null {
	const normalized = normalize(raw);
	const parsed = evaluate(normalized) ?? evaluate(repair(normalized));
	if (parsed !== null) return parsed;

	const first = normalized.match(/-?\d+(?:\.\d+)?/);
	if (!first) return null;
	const value = Number(first[0]);
	return Number.isFinite(value) ? value : null;
}

/**
 * True when the text is an actual calculation rather than a plain number.
 * The leading character is skipped so "-5" counts as a number, not a
 * subtraction.
 */
export function containsOperator(raw: string): boolean {
	return /[+\-*/()]/.test(normalize(raw).slice(1));
}

export type RoundingMode = "round" | "ceil" | "floor";

/** Fraction digits, clamped to what `toFixed` and `Intl` both accept. */
function resolveDecimals(opts: CalculationInputOptions): number {
	return Math.min(Math.max(Math.trunc(opts.decimals ?? 2), 0), 15);
}

/**
 * Reduce a result to `decimals` fraction digits. `Math.ceil` and `Math.floor`
 * only produce integers, so the value is scaled first — and snapped to twelve
 * significant digits before the scaling is undone, because a binary artefact
 * one ulp above the intended value (1.1 * 100 is 110.00000000000001) rounds a
 * whole step up otherwise.
 */
function roundTo(value: number, decimals: number, mode: RoundingMode): number {
	if (mode === "round") return Number(value.toFixed(decimals));
	const factor = 10 ** decimals;
	const scaled = Number((value * factor).toPrecision(12));
	return (mode === "ceil" ? Math.ceil(scaled) : Math.floor(scaled)) / factor;
}

/* ------------------------------------------------------------------ *
 * DOM helpers
 * ------------------------------------------------------------------ */

/**
 * Writing `el.value = x` directly is invisible to React and to
 * react-hook-form. Going through the prototype setter and dispatching a
 * bubbling `input` event makes both of them see the new value.
 */
function setNativeValue(el: HTMLInputElement, value: string, notify = true): void {
	if (el.value === value) return;
	const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, "value")?.set;
	if (setter) setter.call(el, value);
	else el.value = value;
	// notify=false is a display-only write: the expression is shown in the
	// field, but form state keeps the last valid number.
	if (notify) el.dispatchEvent(new Event("input", { bubbles: true }));
}

/**
 * Insert text at the caret, replacing the selection, the way a native paste
 * would — but through the value setter, so React and react-hook-form see it.
 */
function insertAtCaret(el: HTMLInputElement, text: string): void {
	const start = el.selectionStart ?? el.value.length;
	const end = el.selectionEnd ?? start;
	setNativeValue(el, el.value.slice(0, start) + text + el.value.slice(end));
	const caret = start + text.length;
	el.setSelectionRange(caret, caret);
}

/**
 * Writes the expression onto the input, and mirrors it onto an ancestor
 * wrapper when one is present — inputs are replaced elements and cannot
 * host ::before / ::after, so the label has to be rendered by a wrapper.
 */
function applyExpressionAttribute(
	el: HTMLInputElement,
	expression: string | null,
	opts: CalculationInputOptions,
): void {
	const attribute = opts.expressionAttribute ?? "data-calculation";
	const targets: Element[] = [el];

	if (opts.mirrorTo !== false) {
		const wrapper = el.closest(opts.mirrorTo ?? "[data-calc-field]");
		if (wrapper && wrapper !== el) targets.push(wrapper);
	}

	for (const target of targets) {
		if (expression) target.setAttribute(attribute, expression);
		else target.removeAttribute(attribute);
	}

	if (opts.setTitle ?? true) {
		if (expression) el.title = expression;
		else el.removeAttribute("title");
	}
}

/**
 * What the field last committed: the text it shows, the number behind it, and
 * the expression that produced it. Per-element scratch space, so no React state,
 * no re-renders and no keys — and the three are written together, because
 * Escape, the focus restore and the double-click swap all read them as a set.
 */
type CommittedField = {
	formatted: string;
	value: number | null;
	expression: string | null;
};

const COMMITTED = new WeakMap<HTMLInputElement, CommittedField>();

/* ------------------------------------------------------------------ *
 * Options
 * ------------------------------------------------------------------ */

export type CalculationInputOptions = {
	/** Fraction digits kept on the committed result. Default 2. */
	decimals?: number;
	/** How the result is reduced to `decimals` digits. Default "round". */
	rounding?: RoundingMode;
	/**
	 * Intl locale used to render the result. Default "de-DE", whose decimal
	 * separator is the comma — so a field with `decimals` above 0 commits
	 * "12,5", which the parser here reads back but `Number()` does not. Bind
	 * such a field through `onValue` rather than reading the raw text.
	 */
	locale?: string;
	/**
	 * Thousands separators in the displayed value. Default false, because a
	 * grouped figure is hard to read in a table cell — not because it cannot be
	 * parsed: "1.234,56" reads back as 1234.56.
	 */
	useGrouping?: boolean;
	/** Clamp the committed result. */
	min?: number;
	max?: number;
	/** Select the whole value on focus, so typing replaces it. Default true. */
	selectOnFocus?: boolean;
	/** Restore the original expression ("12 * 2,5") when the field is focused again. */
	keepExpression?: boolean;
	/** Attribute that holds the expression. Default "data-calculation". */
	expressionAttribute?: string;
	/**
	 * Selector for an ancestor that should also carry the attribute, so CSS can
	 * render it via ::after. Default "[data-calc-field]"; pass false to skip.
	 */
	mirrorTo?: string | false;
	/** Also set the native title attribute, for a zero-CSS hover fallback. Default true. */
	setTitle?: boolean;
	/** Double-click toggles between the result and the expression. Default true. */
	swapOnDoubleClick?: boolean;
	/** Called after every commit, with the number the field now holds. */
	onValue?: (value: number | null, context: CommitContext) => void;
	/**
	 * Called when the text was not a valid expression as typed. It has been
	 * reduced to a number by the time this runs, so this is for a hint or a
	 * toast — never for recovery.
	 */
	onInvalid?: (expression: string, el: HTMLInputElement) => void;
};

export type CommitContext = {
	/** What the user actually typed. */
	expression: string;
	/** The formatted string now in the field. */
	formatted: string;
	/** True if min/max clamping changed the result. */
	clamped: boolean;
	el: HTMLInputElement;
};

/** Handlers we chain rather than replace. */
type ChainableProps = {
	onBlur?: FocusEventHandler<HTMLInputElement>;
	onFocus?: FocusEventHandler<HTMLInputElement>;
	onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
	onDoubleClick?: MouseEventHandler<HTMLInputElement>;
	onPaste?: ClipboardEventHandler<HTMLInputElement>;
	onDrop?: DragEventHandler<HTMLInputElement>;
};

export type CalculationInputProps = {
	type: "text";
	inputMode: "text";
	autoComplete: "off";
	onBlur: FocusEventHandler<HTMLInputElement>;
	onFocus: FocusEventHandler<HTMLInputElement>;
	onKeyDown: KeyboardEventHandler<HTMLInputElement>;
	onDoubleClick: MouseEventHandler<HTMLInputElement>;
	onPaste: ClipboardEventHandler<HTMLInputElement>;
	onDrop: DragEventHandler<HTMLInputElement>;
};

const OPTION_KEYS = [
	"decimals",
	"rounding",
	"locale",
	"useGrouping",
	"min",
	"max",
	"selectOnFocus",
	"keepExpression",
	"expressionAttribute",
	"mirrorTo",
	"setTitle",
	"swapOnDoubleClick",
	"onValue",
	"onInvalid",
] as const satisfies readonly (keyof CalculationInputOptions)[];

/* ------------------------------------------------------------------ *
 * Hook
 * ------------------------------------------------------------------ */

export function useCalculationInput(defaults: CalculationInputOptions = {}) {
	// Kept in a ref so registerCalculationInput stays referentially stable
	// even when callers pass inline option objects.
	const defaultsRef = useRef(defaults);
	defaultsRef.current = defaults;

	const format = useCallback((n: number, opts: CalculationInputOptions) => {
		return new Intl.NumberFormat(opts.locale ?? "de-DE", {
			maximumFractionDigits: resolveDecimals(opts),
			useGrouping: opts.useGrouping ?? false,
		}).format(n);
	}, []);

	/** Empties the field and reports it, for the user who cleared it deliberately. */
	const clear = useCallback((el: HTMLInputElement, opts: CalculationInputOptions) => {
		const expression = el.value;
		setNativeValue(el, "");
		COMMITTED.set(el, { formatted: "", value: null, expression: null });
		applyExpressionAttribute(el, null, opts);
		opts.onValue?.(null, { expression, formatted: "", clamped: false, el });
	}, []);

	/**
	 * Restores the last committed result, for text carrying no number at all.
	 * The expression behind that result still stands, so the memory and the
	 * attribute are left as they were.
	 */
	const restore = useCallback((el: HTMLInputElement, opts: CalculationInputOptions) => {
		const expression = el.value;
		const committed = COMMITTED.get(el);
		setNativeValue(el, committed?.formatted ?? "");
		opts.onInvalid?.(expression, el);
		opts.onValue?.(committed?.value ?? null, {
			expression,
			formatted: committed?.formatted ?? "",
			clamped: false,
			el,
		});
	}, []);

	/**
	 * Evaluate the field's current text and write the result back. The field
	 * never keeps text that is not a number afterwards: a fumbled expression is
	 * repaired, and text with no number in it falls back to the last committed
	 * result. Returns true when the text was valid as typed.
	 */
	const commit = useCallback(
		(el: HTMLInputElement, opts: CalculationInputOptions): boolean => {
			const expression = el.value;

			if (expression.trim() === "") {
				clear(el, opts);
				return true;
			}

			const wasValid = evaluateExpression(expression) !== null;
			const result = coerceToNumber(expression);
			if (result === null) {
				restore(el, opts);
				return false;
			}

			const decimals = resolveDecimals(opts);
			let value = roundTo(result, decimals, opts.rounding ?? "round");
			let clamped = false;
			if (opts.min !== undefined && value < opts.min) {
				value = opts.min;
				clamped = true;
			}
			if (opts.max !== undefined && value > opts.max) {
				value = opts.max;
				clamped = true;
			}
			// Intl spells a negative zero "-0", and a field showing a minus in front
			// of nothing reads as broken.
			if (Object.is(value, -0)) value = 0;

			const formatted = format(value, opts);
			const wasCalculation = containsOperator(expression);
			setNativeValue(el, formatted);
			COMMITTED.set(el, {
				formatted,
				value,
				expression: wasCalculation ? expression.trim() : null,
			});
			applyExpressionAttribute(el, wasCalculation ? expression.trim() : null, opts);
			opts.onValue?.(value, { expression, formatted, clamped, el });
			if (!wasValid) opts.onInvalid?.(expression, el);
			return wasValid;
		},
		[clear, format, restore],
	);

	/**
	 * Spread onto any input. Extra props are passed straight through, and
	 * onBlur / onFocus / onKeyDown / onPaste / onDrop you supply are chained,
	 * not replaced — so `registerCalculationInput(register("quantity"))` works.
	 */
	const registerCalculationInput = useCallback(
		<P extends Record<string, unknown> & ChainableProps>(
			props?: P & CalculationInputOptions,
		): Omit<P, keyof CalculationInputOptions> & CalculationInputProps => {
			const incoming = (props ?? {}) as P & CalculationInputOptions;

			const local: CalculationInputOptions = {};
			const passthrough: Record<string, unknown> = {};
			for (const [key, val] of Object.entries(incoming)) {
				if ((OPTION_KEYS as readonly string[]).includes(key)) {
					(local as Record<string, unknown>)[key] = val;
				} else {
					passthrough[key] = val;
				}
			}

			const resolve = (): CalculationInputOptions => ({
				...defaultsRef.current,
				...local,
			});

			const onFocus = (event: FocusEvent<HTMLInputElement>) => {
				const opts = resolve();
				const el = event.currentTarget;
				const expression = COMMITTED.get(el)?.expression;
				if (opts.keepExpression && expression && expression !== el.value) {
					setNativeValue(el, expression);
				}
				if (opts.selectOnFocus ?? true) el.select();
				incoming.onFocus?.(event);
			};

			const onBlur = (event: FocusEvent<HTMLInputElement>) => {
				commit(event.currentTarget, resolve());
				// chained last, so validation libraries read the committed value
				incoming.onBlur?.(event);
			};

			const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
				const el = event.currentTarget;

				if (event.key === "Enter") {
					// Enter resolves the calculation the field holds; a field already
					// showing its result lets the keypress through to the form.
					if (containsOperator(el.value)) event.preventDefault();
					commit(el, resolve());
					incoming.onKeyDown?.(event);
					return;
				}

				if (event.key === "Escape") {
					const committed = COMMITTED.get(el);
					if (committed) {
						event.preventDefault();
						setNativeValue(el, committed.formatted);
					}
					incoming.onKeyDown?.(event);
					return;
				}

				// A character the expression alphabet has no place for never enters the
				// field, so there is no letter to strip out again later. Single-character
				// keys only: everything else is navigation, editing or a shortcut, and
				// a modifier means copy, paste or select-all rather than typing.
				const isTypedCharacter =
					event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey;
				if (isTypedCharacter && !EXPRESSION_CHARACTER.test(event.key)) event.preventDefault();

				incoming.onKeyDown?.(event);
			};

			const onPaste = (event: ClipboardEvent<HTMLInputElement>) => {
				const pasted = event.clipboardData?.getData("text") ?? "";
				const filtered = keepExpressionCharacters(pasted);
				// A clean paste is left to the browser, which keeps its own undo stack
				// intact; only one carrying letters has to be re-inserted by hand.
				if (filtered !== pasted) {
					event.preventDefault();
					insertAtCaret(event.currentTarget, filtered);
				}
				incoming.onPaste?.(event);
			};

			const onDrop = (event: DragEvent<HTMLInputElement>) => {
				const dropped = event.dataTransfer?.getData("text") ?? "";
				const filtered = keepExpressionCharacters(dropped);
				if (dropped && filtered !== dropped) {
					event.preventDefault();
					insertAtCaret(event.currentTarget, filtered);
				}
				incoming.onDrop?.(event);
			};

			const onDoubleClick = (event: MouseEvent<HTMLInputElement>) => {
				const opts = resolve();
				const el = event.currentTarget;
				const committed = COMMITTED.get(el);

				if ((opts.swapOnDoubleClick ?? true) && committed?.expression) {
					const expression = committed.expression;
					const showingExpression = el.value.trim() === expression.trim();
					const next = showingExpression ? committed.formatted : expression;

					event.preventDefault();
					// Swapping the expression in is display-only; swapping the result
					// back is a real value, so it notifies.
					setNativeValue(el, next, showingExpression);
					// Overrides the native double-click word selection: caret to the
					// end, ready for "+ 8".
					const caret = next.length;
					el.setSelectionRange(caret, caret);
				}

				incoming.onDoubleClick?.(event);
			};

			return {
				...(passthrough as Omit<P, keyof CalculationInputOptions>),
				type: "text",
				// A decimal keypad carries no operators, so the field would accept
				// numbers and refuse calculations on a phone.
				inputMode: "text",
				autoComplete: "off",
				onFocus,
				onBlur,
				onKeyDown,
				onPaste,
				onDrop,
				onDoubleClick,
			};
		},
		[commit],
	);

	return {
		registerCalculationInput,
		/** Strict: the number the text spells out, or null when it spells none. */
		evaluateExpression,
		/** Lenient: the number behind any text — for normalizing a value before submit. */
		coerceToNumber,
		/** Same formatter the hook uses, for rendering defaultValue. */
		formatNumber: useCallback(
			(n: number | null, opts: CalculationInputOptions = {}) =>
				n === null ? "" : format(n, { ...defaultsRef.current, ...opts }),
			[format],
		),
	};
}
